import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { ethers } from 'ethers';
import { 
  Card, 
  CardHeader, 
  Form, 
  InputGroup, 
  Label, 
  Input, 
  Button, 
  Alert,
  StatGrid,
  StatCard,
  LoadingSpinner
} from './StyledComponents';
import { formatNumber } from '../utils/formatters';
import {
  RAY, mintDart, debtFloorViolation,
  repayDart, repayJoinWad, repayLeavesDust, debtWadFromArt, FULL_REPAY_TOL,
  borrowPlan, liquidationPriceWad, wadToNumber, numberToWad
} from '../utils/vaultMath';
import { txOverrides } from '../utils/tx';

// Amount strings round-trip through floats (suggestions, Max buttons) and can carry
// >18 decimals or exponent notation that parseEther rejects — clamp to WAD safely.
const toWad = (value) => ethers.parseEther(Number(value).toFixed(18));

const VaultActionsV2 = ({
  vaultData,
  systemData,
  balances,
  isLoading,
  userAddress,
  contracts, // We'll need access to contracts for individual operations
  onRefresh // Add refresh function prop
}) => {const [currentOperation, setCurrentOperation] = useState(null); // 'deposit-pls', 'withdraw-pls', 'mint-jdai', 'repay-jdai'
  const [currentStep, setCurrentStep] = useState(0);
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [internalBalances, setInternalBalances] = useState({
    dai: '0',
    gem: '0'
  });  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [approvedAmounts, setApprovedAmounts] = useState({}); // Store approved amounts for display
  const [resumeDismissed, setResumeDismissed] = useState(false); // "Start Fresh" hides the resume banner
  // True while the auto-skip effect is querying the chain for an existing allowance /
  // hope — holds the action button so the user can't sign a redundant authorization.
  const [checkingAuth, setCheckingAuth] = useState(false);
  // Borrow flow: the JDAI goal lives in `amount`; the computed PLS for the deposit
  // legs lives here. `safetyPct` is the target collateralization; `borrowRaw` holds
  // the raw urn/ilk BigInts the planner needs (the float mirrors in systemData are
  // not precise enough to size a deposit).
  const [depositPls, setDepositPls] = useState('');
  const [safetyPct, setSafetyPct] = useState(200);
  const [borrowRaw, setBorrowRaw] = useState(null);
  // Native PLS held back from "Max" deposits so the remaining wizard txs stay payable.
  // Refined from live fee data below; the fallback covers a few txs at typical gas.
  const [gasReservePls, setGasReservePls] = useState(2500);

  // Operation definitions - each operation broken into clear steps
  const OPERATIONS = {
    // Goal-seek flow: the user picks the JDAI they want; the wizard computes the PLS
    // deposit for the chosen safety level and runs deposit + mint end to end. Step
    // ids deliberately match the single-purpose flows so all executeStep behavior
    // (idempotency guards, hope auto-skip, exit caps) carries over unchanged.
    'borrow-jdai': {
      title: 'Borrow JDAI',
      description: 'Pick the JDAI you want — the wizard computes the PLS and runs every step',
      steps: [
        {
          id: 'input-amount',
          title: 'Choose JDAI',
          description: 'How much JDAI do you want to borrow?',
          action: 'borrow-input'
        },
        {
          id: 'join-pls',
          title: 'Deposit PLS to ETHJoin',
          description: 'Transfer the computed PLS from your wallet to the ETHJoin contract',
          action: 'join',
          contract: 'ethjoin'
        },
        {
          id: 'frob-deposit',
          title: 'Add to Vault',
          description: 'Move PLS from ETHJoin to your vault as collateral',
          action: 'frob',
          contract: 'vat'
        },
        {
          id: 'frob-mint',
          title: 'Mint JDAI',
          description: 'Create new JDAI debt in your vault',
          action: 'frob',
          contract: 'vat'
        },
        {
          id: 'hope-daijoin',
          title: 'Authorize DaiJoin',
          description: 'One-time permission letting DaiJoin move your internal JDAI to your wallet',
          action: 'hope',
          contract: 'vat'
        },
        {
          id: 'exit-jdai',
          title: 'Exit to Wallet',
          description: 'Transfer JDAI from internal balance to your wallet',
          action: 'exit',
          contract: 'daijoin'
        },
        {
          id: 'complete',
          title: 'Complete',
          description: 'JDAI borrowed and in your wallet!',
          action: 'complete'
        }
      ]
    },    'deposit-pls': {
      title: 'Deposit PLS Collateral',
      description: 'Add PLS as collateral to your vault',
      steps: [
        {
          id: 'input-amount',
          title: 'Enter Amount',
          description: 'How much PLS do you want to deposit?',
          action: 'input'
        },
        {
          id: 'join-pls',
          title: 'Deposit PLS to ETHJoin',
          description: 'Transfer PLS from your wallet to the ETHJoin contract',
          action: 'join',
          contract: 'ethjoin'
        },
        {
          id: 'frob-deposit',
          title: 'Add to Vault',
          description: 'Move PLS from ETHJoin to your vault as collateral',
          action: 'frob',
          contract: 'vat'
        },
        {
          id: 'complete',
          title: 'Complete',
          description: 'PLS successfully deposited as collateral!',
          action: 'complete'
        }
      ]
    },    'withdraw-pls': {
      title: 'Withdraw PLS Collateral',
      description: 'Remove PLS collateral from your vault',
      steps: [
        {
          id: 'input-amount',
          title: 'Enter Amount',
          description: 'How much PLS do you want to withdraw?',
          action: 'input'
        },
        {
          id: 'frob-withdraw',
          title: 'Remove from Vault',
          description: 'Move PLS from your vault to ETHJoin',
          action: 'frob',
          contract: 'vat'
        },
        {
          id: 'exit-pls',
          title: 'Exit to Wallet',
          description: 'Transfer PLS from ETHJoin to your wallet',
          action: 'exit',
          contract: 'ethjoin'
        },
        {
          id: 'complete',
          title: 'Complete',
          description: 'PLS successfully withdrawn to your wallet!',
          action: 'complete'
        }
      ]
    },
    'mint-jdai': {
      title: 'Mint JDAI',
      description: 'Borrow JDAI against your PLS collateral',
      steps: [
        {
          id: 'input-amount',
          title: 'Enter Amount',
          description: 'How much JDAI do you want to mint?',
          action: 'input'
        },
        {
          id: 'frob-mint',
          title: 'Mint JDAI',
          description: 'Create new JDAI debt in your vault',
          action: 'frob',
          contract: 'vat'
        },
        {
          // DaiJoin.exit moves internal JDAI on the user's behalf, which the Vat only
          // permits after a one-time hope(). Without this, first-time mints revert
          // "Vat/not-allowed" at the exit step. Auto-skipped once granted.
          id: 'hope-daijoin',
          title: 'Authorize DaiJoin',
          description: 'One-time permission letting DaiJoin move your internal JDAI to your wallet',
          action: 'hope',
          contract: 'vat'
        },
        {
          id: 'exit-jdai',
          title: 'Exit to Wallet',
          description: 'Transfer JDAI from internal balance to your wallet',
          action: 'exit',
          contract: 'daijoin'
        },
        {
          id: 'complete',
          title: 'Complete',
          description: 'JDAI successfully minted and transferred to your wallet!',
          action: 'complete'
        }
      ]
    },    'repay-jdai': {
      title: 'Repay JDAI Debt',
      description: 'Pay back JDAI to reduce your vault debt',
      steps: [
        {
          id: 'input-amount',
          title: 'Enter Amount',
          description: 'How much JDAI do you want to repay?',
          action: 'input'
        },
        {
          id: 'approve-jdai',
          title: 'Approve JDAI',
          description: 'Allow the system to access your JDAI tokens',
          action: 'approve',
          contract: 'jdai'
        },
        {
          id: 'join-jdai',
          title: 'Deposit JDAI',
          description: 'Move JDAI from your wallet to the internal system',
          action: 'join',
          contract: 'daijoin'
        },
        {
          id: 'frob-repay',
          title: 'Repay Debt',
          description: 'Use deposited JDAI to reduce your vault debt',
          action: 'frob',
          contract: 'vat'
        },
        {
          id: 'complete',
          title: 'Complete',
          description: 'JDAI debt successfully repaid!',
          action: 'complete'
        }
      ]
    }
  };

  // Get current operation and step
  const operation = currentOperation ? OPERATIONS[currentOperation] : null;
  const step = operation ? operation.steps[currentStep] : null;

  // Borrow deposit legs move (and display) the computed PLS, not the JDAI goal.
  const stepUsesDepositLeg = currentOperation === 'borrow-jdai' && step &&
    (step.id === 'join-pls' || step.id === 'frob-deposit');
  const displayAmount = stepUsesDepositLeg ? depositPls : amount;
  const displayUnit = stepUsesDepositLeg ? 'PLS'
    : currentOperation && currentOperation.includes('pls') ? 'PLS' : 'JDAI';

  // Resolve a step's index by id — jump targets must not be hardcoded positions,
  // or inserting a step silently lands shortcuts on the wrong action.
  const stepIndexById = (operationKey, stepId) => {
    const op = OPERATIONS[operationKey];
    const idx = op ? op.steps.findIndex((s) => s.id === stepId) : -1;
    return idx >= 0 ? idx : 0;
  };
  // Save operation state to localStorage
  const saveOperationState = () => {
    if (currentOperation && userAddress) {
      const state = {
        operation: currentOperation,
        step: currentStep,        amount,
        depositPls,
        safetyPct,
        completedSteps: Array.from(completedSteps),
        approvedAmounts,
        timestamp: Date.now()
      };
      localStorage.setItem(`jdai-operation-${userAddress}`, JSON.stringify(state));
    }
  };
  // Load operation state from localStorage
  const loadOperationState = () => {
    if (!userAddress) return;
    
    try {
      const saved = localStorage.getItem(`jdai-operation-${userAddress}`);
      if (saved) {
        const state = JSON.parse(saved);
        // Only restore if less than 1 hour old
        if (Date.now() - state.timestamp < 3600000) {
          // A state parked on the final "complete" step is a finished operation the
          // user never clicked Done on — clear it instead of resuming into it.
          const op = OPERATIONS[state.operation];
          if (!op || state.step >= op.steps.length - 1) {
            localStorage.removeItem(`jdai-operation-${userAddress}`);
            return false;
          }
          setCurrentOperation(state.operation);
          setCurrentStep(state.step);          setAmount(state.amount || '');
          setDepositPls(state.depositPls || '');
          setSafetyPct(state.safetyPct || 200);
          setCompletedSteps(new Set(state.completedSteps || []));
          setApprovedAmounts(state.approvedAmounts || {});
          return true;
        } else {
          // Clear expired state
          localStorage.removeItem(`jdai-operation-${userAddress}`);
        }
      }
    } catch (error) {
      console.error('Error loading operation state:', error);
    }
    return false;
  };

  // Clear operation state from localStorage
  const clearOperationState = () => {
    if (userAddress) {
      localStorage.removeItem(`jdai-operation-${userAddress}`);
    }
  };  // Load saved operation state on mount and whenever the account changes
  useEffect(() => {
    if (userAddress) {
      setResumeDismissed(false);
      loadOperationState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAddress]);  // Save operation state when it changes
  useEffect(() => {
    saveOperationState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperation, currentStep, amount, completedSteps, approvedAmounts, userAddress]);

  // Start an operation from scratch (menu tiles). Resets are explicit here rather than
  // in an effect watching currentOperation — that effect also fired one render after
  // loadOperationState() and the menu-banner jumps, wiping the restored/target step
  // back to 0. It's why "Resume" never resumed and the jumps needed setTimeout hacks.
  const startOperation = (operationKey) => {
    setCurrentOperation(operationKey);
    setCurrentStep(0);
    setAmount('');
    setDepositPls('');
    setSafetyPct(200);
    setCompletedSteps(new Set());
    setApprovedAmounts({});
  };

  // Jump into the middle of an operation with a prefilled amount (menu banners for
  // balances already inside the system). Same-batch state updates — no setTimeout.
  const jumpToStep = (operationKey, stepId, prefillAmount) => {
    setCurrentOperation(operationKey);
    setCurrentStep(stepIndexById(operationKey, stepId));
    setAmount(prefillAmount);
    setDepositPls('');
    setCompletedSteps(new Set());
    setApprovedAmounts({});
  };

  // Fetch internal balances. Hoisted so executeStep can refetch immediately after a
  // transaction lands — the confirmation alerts read these values, and waiting for
  // the 10s poll left users staring at a step with no proof it worked.
  const fetchInternalBalances = useCallback(async () => {
    if (!contracts || !userAddress) return;

    try {
      const ilk = ethers.encodeBytes32String("PLS-A");
      const daiBalance = await contracts.vat.dai(userAddress);
      const gemBalance = await contracts.vat.gem(ilk, userAddress);

      setInternalBalances({
        dai: ethers.formatUnits(daiBalance, 45),
        gem: ethers.formatEther(gemBalance)
      });
    } catch (error) {
      console.error('Error fetching internal balances:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, userAddress]);

  useEffect(() => {
    fetchInternalBalances();

    // Refresh internal balances every 10 seconds
    const interval = setInterval(fetchInternalBalances, 10000);
    return () => clearInterval(interval);
  }, [fetchInternalBalances]);

  // Raw urn/ilk values for the borrow planner, fetched once on entering the flow.
  useEffect(() => {
    if (currentOperation !== 'borrow-jdai' || !contracts || !userAddress) return;
    let cancelled = false;
    (async () => {
      try {
        const ilk = ethers.encodeBytes32String("PLS-A");
        const [ink, art] = await contracts.vat.urns(ilk, userAddress);
        const ilkData = await contracts.vat.ilks(ilk);
        if (!cancelled) setBorrowRaw({ ink, art, rate: ilkData.rate, spot: ilkData.spot });
      } catch (e) {
        console.error('Error fetching borrow planning data:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperation, userAddress, !!contracts]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
  };
  const getMaxAmount = () => {
    switch (currentOperation) {
      case 'borrow-jdai': {
        // Whole-number JDAI affordable from current vault + wallet at the chosen
        // safety level, capped by the remaining ilk ceiling. spot embeds mat and
        // par, so ink·spot is the max tab at the enforced minimum; scale by
        // mat/target for the chosen safety, minus the 0.1% plan pad.
        if (!borrowRaw) return 0;
        const walletAvail = Math.max(0, parseFloat(balances.pls) - gasReservePls);
        const inkTotal = wadToNumber(borrowRaw.ink) + walletAvail;
        const spotNum = Number(borrowRaw.spot) / 1e27;
        const mat = parseFloat(systemData.liquidationRatio) || 1.5;
        const capacity = (inkTotal * spotNum * mat / (safetyPct / 100)) / 1.001;
        const existingDebt = wadToNumber(debtWadFromArt(borrowRaw.art, borrowRaw.rate));
        const room = parseFloat(systemData.availableDebt || '0');
        return Math.max(0, Math.floor(Math.min(capacity - existingDebt, room)));
      }
      case 'deposit-pls':
        // Use native PLS balance from wallet (not WPLS token balance), keeping a
        // fee-data-sized reserve so the remaining wizard txs stay payable
        return Math.max(0, parseFloat(balances.pls) - gasReservePls);
      case 'withdraw-pls':
        // Calculate safe withdrawal amount
        return calculateMaxSafeWithdrawal();
      case 'mint-jdai': {
        // 0.1% haircut keeps the frob off the exact collateral-ratio boundary (the
        // Vat's check is >=, and mintDart rounds art up a wei); also respect the
        // remaining ilk debt ceiling.
        const headroom = Math.max(0, parseFloat(vaultData.maxDebt) - parseFloat(vaultData.debt)) * 0.999;
        const ceilingRoom = parseFloat(systemData.availableDebt || '0');
        return Math.max(0, Math.min(headroom, ceilingRoom));
      }      case 'repay-jdai':
        // Use wallet JDAI balance, limited by vault debt
        const walletJdai = parseFloat(balances.jdai);
        const vaultDebt = parseFloat(vaultData.debt);
        return Math.min(walletJdai, vaultDebt);
      default:
        return 0;
    }
  };

  const calculateMaxSafeWithdrawal = () => {
    if (parseFloat(vaultData.debt) === 0) {
      return parseFloat(vaultData.collateral);
    }

    const currentCollateral = parseFloat(vaultData.collateral);
    const currentDebt = parseFloat(vaultData.debt);
    const plsPrice = parseFloat(systemData.oraclePlsPrice);
    const jdaiPrice = parseFloat(systemData.oracleJdaiTarget);
    const safetyRatio = 1.6;
    
    const minCollateralNeeded = (currentDebt * jdaiPrice * safetyRatio) / plsPrice;
    return Math.max(0, currentCollateral - minCollateralNeeded);
  };  // Get actual approved amount for display
  const getApprovedAmount = async (stepId) => {
    if (!contracts || !userAddress) return null;
    
    try {
      let allowance;
      if (stepId === 'approve-jdai') {
        allowance = await contracts.jdai.allowance(userAddress, contracts.daijoin.target);
        // Convert from wei to ether and format as string to avoid scientific notation
        return parseFloat(ethers.formatEther(allowance)).toString();
      }
    } catch (error) {
      console.error('Error getting approved amount:', error);
    }
    return null;
  };

  // Auto-detect existing balances and suggest appropriate amounts
  const detectSuggestedAmount = () => {
    if (!currentOperation || !internalBalances) return null;

    switch (currentOperation) {
      case 'deposit-pls':
        // If there's PLS in internal balance, suggest using that amount
        const gemBalance = parseFloat(internalBalances.gem);
        if (gemBalance > 0) {
          return {
            amount: gemBalance.toString(),
            reason: `${parseFloat(gemBalance).toLocaleString('en-US', { 
              minimumFractionDigits: 6, 
              maximumFractionDigits: 6 
            })} PLS found in internal balance from previous deposit`,
            skipInput: false // Let user confirm the amount
          };
        }
        break;
        
      case 'withdraw-pls':
        // For withdrawals, suggest based on vault collateral
        const collateral = parseFloat(vaultData.collateral);
        if (collateral > 0) {
          const maxSafe = calculateMaxSafeWithdrawal();
          return {
            amount: Math.min(collateral, maxSafe).toString(),
            reason: `Based on your ${parseFloat(collateral).toLocaleString('en-US', { 
              minimumFractionDigits: 6, 
              maximumFractionDigits: 6 
            })} PLS vault collateral`,
            skipInput: false
          };
        }
        break;
        
      case 'mint-jdai':
        // Check if there's existing internal DAI balance
        const daiBalance = parseFloat(internalBalances.dai);
        if (daiBalance > 0) {
          return {
            amount: daiBalance.toString(),
            reason: `${parseFloat(daiBalance).toLocaleString('en-US', { 
              minimumFractionDigits: 6, 
              maximumFractionDigits: 6 
            })} JDAI found in internal balance`,
            skipInput: false
          };
        }
        break;      case 'repay-jdai':
        // Use simplified logic: just use whatever is in the internal DAI balance
        // Check if there's any internal DAI balance available
        const daiBalanceFormatted = parseFloat(internalBalances.dai);
        
        if (daiBalanceFormatted > 0) {
          // Skip input - we'll use the full internal balance
          return {
            amount: daiBalanceFormatted.toString(),
            reason: `Will use all ${daiBalanceFormatted.toLocaleString('en-US', { 
              minimumFractionDigits: 6, 
              maximumFractionDigits: 6 
            })} JDAI from internal balance`,
            skipInput: true // Skip the input step
          };
        } else {
          return {
            amount: '0',
            reason: 'No JDAI available in internal balance for repayment',
            skipInput: false // Still show the input to display the error
          };
        }
      default:
        break;
    }

    return null;
  };
  // Size the gas reserve from live fee data: 4 remaining wizard txs at ~300k gas each,
  // capped the same way txOverrides caps fees (2x). PulseChain gas runs hundreds of PLS
  // per tx — the old fixed 0.01 PLS reserve stranded "Max" deposits mid-wizard.
  useEffect(() => {
    if (!contracts) return;
    let cancelled = false;
    (async () => {
      try {
        const fee = await contracts.vat.runner.provider.getFeeData();
        const perGas = fee.maxFeePerGas ?? fee.gasPrice;
        if (!cancelled && perGas) {
          const reserve = Number(ethers.formatEther(perGas * 2n * 300000n * 4n));
          setGasReservePls(Math.max(100, Math.ceil(reserve)));
        }
      } catch (e) { /* keep the fallback reserve */ }
    })();
    return () => { cancelled = true; };
  }, [contracts]);

  // Stupid-proofing: auto-skip permission steps whose on-chain precondition is already
  // met — a JDAI allowance covering the amount, or a Vat hope() already granted. Both
  // are unambiguous on-chain preconditions, so the skip is safe.
  useEffect(() => {
    if (!contracts || !userAddress || !step) return;
    if (step.action !== 'approve' && step.action !== 'hope') return;
    if (step.action === 'approve' && (!amount || parseFloat(amount) <= 0)) return;
    let cancelled = false;
    setCheckingAuth(true);
    (async () => {
      try {
        let satisfied = false;
        if (step.action === 'approve') {
          const allowance = await contracts.jdai.allowance(userAddress, contracts.daijoin.target);
          satisfied = allowance >= toWad(amount) + FULL_REPAY_TOL;
        } else {
          satisfied = (await contracts.vat.can(userAddress, contracts.daijoin.target)) === 1n;
        }
        if (!cancelled && satisfied) {
          toast.success('Already authorized — skipping ahead', { id: 'auth-skip' });
          setCompletedSteps((prev) => new Set([...prev, step.id]));
          setCurrentStep((s) => (operation ? Math.min(s + 1, operation.steps.length - 1) : s));
        }
      } catch (e) { /* leave the user on the step to authorize manually */
      } finally {
        if (!cancelled) setCheckingAuth(false);
      }
    })();
    return () => { cancelled = true; setCheckingAuth(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperation, currentStep, contracts, userAddress, amount]);

  const executeStep = async () => {
    if (!step || !contracts) return;

    // Safety check: ensure amount is set and valid (hope is a pure permission grant
    // and doesn't involve an amount)
    if (step.action !== 'hope' && (!amount || amount.trim() === '' || parseFloat(amount) <= 0)) {
      toast.error('Please enter a valid amount before proceeding', { id: `step-${step.id}` });
      return;
    }

    // Debt-floor ("dust") guard: never let a mint/repay leave the vault below the floor —
    // it would revert on-chain and, for repay, strand JDAI mid-sequence. Checked on the
    // debt-changing step (mint frob — also the borrow flow's) and on every repay step
    // (so we stop before approve).
    const mintLike = currentOperation === 'mint-jdai' || currentOperation === 'borrow-jdai';
    if (mintLike ? step.id === 'frob-mint' : currentOperation === 'repay-jdai') {
      const floorMsg = debtFloorViolation(mintLike ? 'mint-jdai' : 'repay-jdai', amount, vaultData.debt, systemData.minDebt);
      if (floorMsg) {
        toast.error(floorMsg, { id: `step-${step.id}` });
        return;
      }
    }

    setIsProcessing(true);
    toast.loading(`${step.description} — confirm in your wallet`, { id: `step-${step.id}` });

    // Two-phase feedback: the loading toast starts as "confirm in wallet"; once the
    // tx is submitted it flips to "confirming" so the user knows whose turn it is.
    const send = async (txPromise) => {
      const tx = await txPromise;
      toast.loading('Transaction sent — waiting for confirmation…', { id: `step-${step.id}` });
      await tx.wait();
      return tx;
    };

    try {
      // Borrow deposit legs move the computed PLS; every other leg uses `amount`.
      if (stepUsesDepositLeg && (!depositPls || parseFloat(depositPls) <= 0)) {
        throw new Error('The PLS amount for this step is missing — go back to step 1 to recompute the plan.');
      }
      const amountWei = step.action === 'hope' ? 0n : toWad(stepUsesDepositLeg ? depositPls : amount);
      const ilk = ethers.encodeBytes32String("PLS-A");
      const ov = await txOverrides(contracts.vat.runner.provider);
      switch (step.action) {
        case 'approve':
          if (step.contract === 'jdai') {
            // Tiny buffer: a full repay's join can exceed the face amount by a few wei
            // (rate accrual + float rounding), and an exact allowance would revert it.
            await send(contracts.jdai.approve(contracts.daijoin.target, amountWei + FULL_REPAY_TOL, ov));
          }
          break;

        case 'hope': {
          // One-time Vat permission for DaiJoin.exit (moves internal JDAI to wallet).
          const can = await contracts.vat.can(userAddress, contracts.daijoin.target);
          if (can !== 1n) {
            await send(contracts.vat.hope(contracts.daijoin.target, ov));
          }
          break;
        }        case 'join':
          if (step.contract === 'ethjoin') {
            // Idempotency: if the internal gem balance already covers this amount, a
            // previous join landed but progress was lost (refresh, expired resume
            // state) — don't pull PLS from the wallet a second time.
            const gemAlready = await contracts.vat.gem(ilk, userAddress);
            if (gemAlready >= amountWei) {
              toast.success('This PLS is already deposited — skipping to the next step', { id: `step-${step.id}` });
              break;
            }
            // Pre-flight checks for ETHJoin to identify potential issues
            try {
              
              // First, test basic contract connectivity
              try {
                const contractCode = await contracts.ethjoin.runner.provider.getCode(contracts.ethjoin.target);
                if (contractCode === '0x') {
                  throw new Error('ETHJoin contract not found at address - no code deployed');
                }
              } catch (codeError) {
                console.error('Contract connectivity test failed:', codeError);
                throw new Error('Cannot connect to ETHJoin contract');
              }
              
              // Check if ETHJoin is live
              const live = await contracts.ethjoin.live();
              if (live.toString() === '0') {
                throw new Error('ETHJoin contract is not live (paused)');
              }
              
              // Check ETHJoin configuration - wrap these in try-catch for individual errors
              let ethjoinVat;
              
              try {
                ethjoinVat = await contracts.ethjoin.vat();
              } catch (vatError) {
                console.error('Failed to get VAT address from ETHJoin:', vatError);
                throw new Error('Cannot read VAT address from ETHJoin contract');
              }
              
              try {
                await contracts.ethjoin.ilk(); // validate ILK is readable
              } catch (ilkError) {
                console.error('Failed to get ILK from ETHJoin:', ilkError);
                throw new Error('Cannot read ILK from ETHJoin contract');
              }
              
              
              if (ethjoinVat.toLowerCase() !== contracts.vat.target.toLowerCase()) {
                throw new Error(`ETHJoin VAT mismatch. Expected: ${contracts.vat.target}, Got: ${ethjoinVat}`);
              }
              
              // Check if ETHJoin has authorization in VAT to call slip()
              // This is critical - VAT must have authorized ETHJoin via rely()
              try {
                
                // Confirm the VAT gem mapping is readable before proceeding
                await contracts.vat.gem(ilk, userAddress);

              } catch (authError) {
                console.error('VAT authorization check failed:', authError);
                throw new Error('ETHJoin may not be authorized in VAT to call slip()');
              }
              
              // Check user's native PLS balance
              const plsBalance = await contracts.ethjoin.runner.provider.getBalance(userAddress);
              
              if (plsBalance < amountWei) {
                throw new Error(`Insufficient PLS balance. Have: ${ethers.formatEther(plsBalance)}, Need: ${ethers.formatEther(amountWei)}`);
              }
              
              // Check if amount is reasonable (not too small or too large)
              if (amountWei === 0n) {
                throw new Error('Cannot transfer zero amount');
              }
              
              // Final gas estimation - this will catch any remaining issues
              try {
                await contracts.ethjoin.join.estimateGas(userAddress, { value: amountWei });
              } catch (gasError) {
                console.error('Gas estimation failed:', gasError);
                // Try to provide more specific error message
                if (gasError.message.includes('ETHJoin/not-live')) {
                  throw new Error('ETHJoin contract is paused');
                } else if (gasError.message.includes('not-authorized')) {
                  throw new Error('ETHJoin is not authorized in VAT contract');
                } else {
                  throw new Error(`Transaction would fail: ${gasError.message}`);
                }
              }
              
              
            } catch (preflightError) {
              console.error('Pre-flight check failed:', preflightError);
              if (preflightError.message.includes('execution reverted')) {
                throw new Error('Transaction would fail - likely due to contract authorization or state issues');
              } else {
                throw preflightError;
              }
            }
            
            await send(contracts.ethjoin.join(userAddress, { value: amountWei, ...ov }));

          } else if (step.contract === 'daijoin') {
            // Size the join so the repay frob can settle: a full repay consumes
            // art * rate internal DAI, which is a hair MORE than the face amount once
            // rate > 1 — joining only the face amount strands the vault in the dust
            // band and the frob reverts "Vat/dust". Credits internal DAI already there.
            const [, art] = await contracts.vat.urns(ilk, userAddress);
            const ilkData = await contracts.vat.ilks(ilk);
            const daiRad = await contracts.vat.dai(userAddress);
            const joinWad = repayJoinWad(amountWei, art, ilkData.rate, daiRad);
            if (joinWad === 0n) {
              toast.success('Internal balance already covers this repay — skipping deposit', { id: `step-${step.id}` });
            } else {
              const walletJdai = await contracts.jdai.balanceOf(userAddress);
              if (walletJdai < joinWad) {
                throw new Error(`Repaying this amount needs ${ethers.formatEther(joinWad)} JDAI in your wallet (you have ${ethers.formatEther(walletJdai)}).`);
              }
              await send(contracts.daijoin.join(userAddress, joinWad, ov));
            }
          }
          break;        case 'exit':
          if (step.contract === 'ethjoin') {
            // Cap at the actual internal gem balance — amounts round-trip through
            // floats and can overshoot the on-chain value by a few wei.
            const gem = await contracts.vat.gem(ilk, userAddress);
            const exitWad = amountWei > gem ? gem : amountWei;
            if (exitWad <= 0n) {
              throw new Error('No PLS available in the internal balance to withdraw.');
            }
            await send(contracts.ethjoin.exit(userAddress, exitWad, ov));
          } else if (step.contract === 'daijoin') {
            // Same cap for internal DAI (RAD -> WAD floor).
            const daiRad = await contracts.vat.dai(userAddress);
            const maxExit = daiRad / RAY;
            const exitWad = amountWei > maxExit ? maxExit : amountWei;
            if (exitWad <= 0n) {
              throw new Error('No JDAI available in the internal balance to move to your wallet.');
            }
            await send(contracts.daijoin.exit(userAddress, exitWad, ov));
          }
          break;        case 'frob':
          // Pre-flight checks for frob operations
          
          // Verify internal balances before frob
          const currentDaiBalance = await contracts.vat.dai(userAddress);

          let dink = 0n, dart = 0n; // Initialized (assuming BigInts)
          
          // Keyed on step.id (shared across operations) so the borrow flow's deposit
          // and mint frobs get identical behavior to the single-purpose flows.
          if (step.id === 'frob-deposit') {
            // For deposit: move PLS from internal balance to vault. Rounding overshoot
            // (float round-trips) caps to the real gem balance; a real shortfall
            // (>0.1%) means the deposit step never ran — error out rather than firing
            // a do-nothing frob that gets marked "complete".
            const gem = await contracts.vat.gem(ilk, userAddress);
            if (gem < (amountWei * 999n) / 1000n) {
              throw new Error(`Only ${ethers.formatEther(gem)} PLS is in the deposit contract — the "Deposit PLS to ETHJoin" step hasn't completed for this amount. Go back one step.`);
            }
            dink = amountWei > gem ? gem : amountWei;
            dart = 0n;         // No debt change
          } else if (step.id === 'frob-withdraw') {
            // For withdrawal: move PLS from vault to internal balance
            dink = -amountWei; // Negative value to remove collateral
            dart = 0n;         // No debt change
          } else if (step.id === 'frob-mint') {
            // Idempotency: if the requested JDAI already sits in the internal balance,
            // the mint frob landed but progress was lost (refresh, expired resume) —
            // minting again would double the debt. Skip straight to withdrawal.
            if (currentDaiBalance >= amountWei * RAY) {
              toast.success('This JDAI is already minted and waiting inside the system — skipping to withdrawal (no new debt created)', { id: `step-${step.id}` });
              break;
            }
            // Draw JDAI accounting for the accumulated stability-fee `rate` so the full
            // requested amount is exitable with no internal-DAI dust left stuck.
            const ilkData = await contracts.vat.ilks(ilk);
            dart = mintDart(amountWei, ilkData.rate);
          } else if (step.id === 'frob-repay') {
            // Settle debt in normalized units. A full-intent repay (request within a
            // hair of the tab, covered by internal DAI) clears art exactly, so the
            // Vat's dust check can't trip; partials are capped by the internal balance
            // and by art, and blocked here if they'd land below the dust floor.
            const urnData = await contracts.vat.urns(ilk, userAddress);
            const ilkData = await contracts.vat.ilks(ilk);
            const d = repayDart(amountWei, urnData.art, ilkData.rate, currentDaiBalance);

            if (d <= 0n) {
              throw new Error('Nothing to repay — no internal JDAI available. Complete the deposit step first.');
            }
            if (repayLeavesDust(d, urnData.art, ilkData.rate, ilkData.dust)) {
              const left = ethers.formatEther(debtWadFromArt(urnData.art - d, ilkData.rate));
              const min = ethers.formatUnits(ilkData.dust, 45);
              throw new Error(`This would leave ${left} JDAI of debt, below the ${min} JDAI minimum. Repay the full debt, or leave at least ${min} JDAI.`);
            }

            dart = -d;
          }

          await send(contracts.vat.frob(ilk, userAddress, userAddress, userAddress, dink, dart, ov));
          break;

        default:
          throw new Error(`Unknown step action: ${step.action}`);
      }      toast.success(step.title + ' completed!', { id: `step-${step.id}` });
      
      // Mark step as completed
      setCompletedSteps(prev => new Set([...prev, step.id]));
      
      // If it's an approval step, store the approved amount for display
      if (step.action === 'approve') {
        const approvedAmount = await getApprovedAmount(step.id);
        if (approvedAmount) {
          setApprovedAmounts(prev => ({ ...prev, [step.id]: approvedAmount }));
        }
      }
      
      // Refresh vault data AND internal balances after a successful transaction —
      // the step confirmation alerts read internalBalances, so it can't wait for
      // the 10s poll.
      fetchInternalBalances();
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh();
      }

      // Move to next step
      if (currentStep < operation.steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }} catch (error) {
      console.error('Step execution error:', error);
      let errorMessage = `Failed to ${step.title.toLowerCase()}`;
      
      if (error.code === 4001) {
        errorMessage = 'Transaction cancelled by user';
      } else if (error.code === 'CALL_EXCEPTION') {
        // Handle specific contract call exceptions
        if (step.action === 'frob') {
          if (step.id === 'frob-repay') {
            errorMessage = 'Failed to repay debt. This may be due to insufficient internal JDAI balance or rate calculation issues. Please verify you have completed the previous steps.';
          } else if (step.id === 'frob-withdraw') {
            errorMessage = 'Failed to withdraw collateral. You may not have enough collateral to withdraw safely, or there may be outstanding debt preventing withdrawal.';
          } else if (step.id === 'frob-deposit') {
            errorMessage = 'Failed to deposit collateral. Please verify PLS is available in the ETHJoin contract.';
          } else if (step.id === 'frob-mint') {
            errorMessage = 'Failed to mint JDAI. You may not have enough collateral to mint this amount safely.';
          }
        } else if (step.action === 'join' && step.contract === 'ethjoin') {
          errorMessage = 'Failed to deposit PLS to ETHJoin. Please check your PLS balance and try again.';
        } else if (step.action === 'exit') {
          errorMessage = 'Failed to withdraw assets. Please verify the internal balance is sufficient.';
        } else if (error.message && error.message.includes('revert')) {
          errorMessage = `Transaction failed: ${error.message}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
        toast.error(errorMessage, { id: `step-${step.id}` });
      
      // Also refresh on error to ensure UI shows current state
      fetchInternalBalances();
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Borrow flow: validate the goal, size the deposit, and either enter the deposit
  // legs or — when existing collateral already covers the target — skip straight to
  // the mint.
  const handleBorrowContinue = () => {
    const floorMsg = debtFloorViolation('mint-jdai', amount, vaultData.debt, systemData.minDebt);
    if (floorMsg) { toast.error(floorMsg); return; }
    if (parseFloat(amount) > parseFloat(systemData.availableDebt || '0')) {
      toast.error(`Only ${formatNumber(systemData.availableDebt, 0)} JDAI of ceiling room is left — choose a smaller amount.`);
      return;
    }
    if (!borrowRaw) { toast.error('Still loading vault data — try again in a moment.'); return; }
    const mat = parseFloat(systemData.liquidationRatio) || 1.5;
    const plan = borrowPlan(toWad(amount), numberToWad(safetyPct / 100), numberToWad(mat), borrowRaw);
    if (!plan) { toast.error('Could not compute a plan — check the amount and try again.'); return; }
    const depositNum = wadToNumber(plan.additionalInkWad);
    if (depositNum > 0 && depositNum + gasReservePls > parseFloat(balances.pls)) {
      toast.error(`This plan needs ${formatNumber(depositNum, 0)} PLS plus ~${formatNumber(gasReservePls, 0)} kept for gas, but your wallet holds ${formatNumber(balances.pls, 0)} PLS.`);
      return;
    }
    if (plan.additionalInkWad === 0n) {
      setDepositPls('0');
      setCompletedSteps((prev) => new Set([...prev, 'join-pls', 'frob-deposit']));
      setCurrentStep(stepIndexById('borrow-jdai', 'frob-mint'));
      toast.success('Your current collateral already covers this borrow — skipping the deposit steps');
    } else {
      // Full-precision string (no float round-trip) — toWad clamps later.
      setDepositPls(ethers.formatEther(plan.additionalInkWad));
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      setCurrentOperation(null);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };  const resetOperation = () => {
    setCurrentOperation(null);
    setCurrentStep(0);
    setAmount('');
    setDepositPls('');
    setCompletedSteps(new Set());
    setApprovedAmounts({});
    clearOperationState();
  };

  if (!currentOperation) {
    // Main menu - show operation selection
    return (
      <Card>
        <CardHeader>
          <h2>Vault Operations Wizard</h2>
          <p>Choose an operation to begin. Each step will be clearly guided.</p>
        </CardHeader>

        {/* Current Vault Status */}
        <StatGrid style={{ marginBottom: '2rem' }}>
          {/* '…' while loading — flashing "0" reads as an empty vault */}
          <StatCard>
            <div className="label">PLS Collateral</div>
            <div className="value">{vaultData.isLoading ? '…' : formatNumber(vaultData.collateral, 2)}</div>
          </StatCard>
          <StatCard>
            <div className="label">JDAI Debt</div>
            <div className="value">{vaultData.isLoading ? '…' : formatNumber(vaultData.debt, 2)}</div>
          </StatCard>
          <StatCard>
            <div className="label">Wallet PLS</div>
            <div className="value">{isLoading ? '…' : formatNumber(balances.pls, 2)}</div>
          </StatCard>
          <StatCard>
            <div className="label">Wallet JDAI</div>
            <div className="value">{isLoading ? '…' : formatNumber(balances.jdai, 2)}</div>
          </StatCard>
        </StatGrid>        {/* Incomplete Operation Detection */}
        {(() => {
          const gemBalance = parseFloat(internalBalances.gem);
          const daiBalance = parseFloat(internalBalances.dai);
            // Define dust thresholds - ignore very small amounts
          const PLS_DUST_THRESHOLD = 0.000001; // Ignore less than 0.000001 PLS (6 decimal places)
          const JDAI_DUST_THRESHOLD = 0.000001; // Ignore less than 0.000001 JDAI (6 decimal places)
            if (gemBalance > PLS_DUST_THRESHOLD) {
            return (
              <Alert variant="info" style={{ marginBottom: '2rem' }}>
                <h3>💰 What would you like to do with your PLS?</h3>
                <p>You have <strong>{parseFloat(gemBalance).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} PLS</strong> in the system. Choose what you'd like to do with it:</p>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button
                    onClick={() => jumpToStep('deposit-pls', 'frob-deposit', gemBalance.toString())}
                    style={{ flex: 1 }}
                  >
                    🏦 Deposit as Collateral
                  </Button>
                  <Button
                    onClick={() => jumpToStep('withdraw-pls', 'exit-pls', gemBalance.toString())}
                    style={{ flex: 1 }}
                  >
                    💳 Return to Wallet
                  </Button>
                </div>
              </Alert>
            );
          }            if (daiBalance > JDAI_DUST_THRESHOLD) {
            return (
              <Alert variant="info" style={{ marginBottom: '2rem' }}>
                <h3>💵 What would you like to do with your JDAI?</h3>
                <p>You have <strong>{parseFloat(daiBalance).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI</strong> in the system. Choose what you'd like to do with it:</p>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button
                    onClick={() =>
                      // JDAI is already minted — go to the hope step (auto-skips to
                      // exit when DaiJoin is already authorized)
                      jumpToStep('mint-jdai', 'hope-daijoin', daiBalance.toString())
                    }
                    style={{ flex: 1 }}
                  >
                    💳 Move to Wallet
                  </Button>                  <Button
                    onClick={() =>
                      // JDAI is already inside the Vat — approve/join would pull wallet
                      // tokens again (or revert with none), so go straight to the frob.
                      jumpToStep('repay-jdai', 'frob-repay',
                        Math.min(daiBalance, parseFloat(vaultData.debt)).toString())
                    }
                    style={{ flex: 1 }}
                  >
                    💰 Use to Repay Debt
                  </Button>
                </div>
              </Alert>
            );
          }
          
          return null;
        })()}

        {/* Resume Operation Alert */}
        {!currentOperation && userAddress && !resumeDismissed && (() => {
          const saved = localStorage.getItem(`jdai-operation-${userAddress}`);
          if (saved) {
            try {
              const state = JSON.parse(saved);
              if (Date.now() - state.timestamp < 3600000) {
                return (
                  <Alert variant="info" style={{ marginBottom: '2rem' }}>
                    <h3>🔄 Resume Previous Operation</h3>
                    <p>You have an incomplete {OPERATIONS[state.operation]?.title} operation from {new Date(state.timestamp).toLocaleTimeString()}.</p>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                      <Button 
                        onClick={() => loadOperationState()}
                        variant="primary"
                      >
                        Resume Operation
                      </Button>
                      <Button
                        onClick={() => {
                          clearOperationState();
                          // localStorage writes don't re-render — drop the banner via state
                          setResumeDismissed(true);
                        }}
                        variant="secondary"
                      >
                        Start Fresh
                      </Button>
                    </div>
                  </Alert>
                );
              }
            } catch (e) {
              // Invalid saved state, clear it
              localStorage.removeItem(`jdai-operation-${userAddress}`);
            }
          }
          return null;
        })()}

        {/* NOTE: the old "Stuck Debt Detected" alert lived here. It fired for anyone
            with debt and zero wallet JDAI (including people who simply sold their
            minted JDAI), and its clear action threw with no visible feedback when the
            internal balance couldn't cover the tab. The StrandedFundsBanner above the
            vault overview supersedes it with dust-floor-aware repay options. */}

        {/* Operation Selection */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Button
            onClick={() => startOperation('borrow-jdai')}
            style={{
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
              gridColumn: '1 / -1'
            }}
          >
            <div style={{ fontSize: '2rem' }}>🎯</div>
            <div style={{ fontWeight: 'bold' }}>Borrow JDAI</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Tell us the JDAI you want — we compute the PLS and guide every step
            </div>
          </Button>

          <Button
            onClick={() => startOperation('deposit-pls')}
            style={{ 
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <div style={{ fontSize: '2rem' }}>💰</div>
            <div style={{ fontWeight: 'bold' }}>Deposit PLS</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Add PLS as collateral
            </div>
          </Button>

          <Button
            onClick={() => startOperation('withdraw-pls')}
            disabled={parseFloat(vaultData.collateral) <= 0}
            style={{ 
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <div style={{ fontSize: '2rem' }}>🏃‍♂️</div>
            <div style={{ fontWeight: 'bold' }}>Withdraw PLS</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Remove PLS collateral
            </div>
          </Button>

          <Button
            onClick={() => startOperation('mint-jdai')}
            disabled={parseFloat(vaultData.collateral) <= 0}
            style={{ 
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <div style={{ fontSize: '2rem' }}>🏭</div>
            <div style={{ fontWeight: 'bold' }}>Mint JDAI</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Borrow against collateral
            </div>
          </Button>

          <Button
            onClick={() => startOperation('repay-jdai')}
            disabled={parseFloat(vaultData.debt) <= 0}
            style={{ 
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <div style={{ fontSize: '2rem' }}>💳</div>
            <div style={{ fontWeight: 'bold' }}>Repay JDAI</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Pay back debt
            </div>
          </Button>
        </div>
      </Card>
    );
  }

  // Wizard interface for selected operation
  return (
    <Card>
      <CardHeader>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>{operation.title}</h2>
            <p>{operation.description}</p>
          </div>
          <Button 
            onClick={resetOperation} 
            variant="secondary"
            style={{ marginLeft: '2rem' }}
          >
            ← Back to Menu
          </Button>
        </div>
      </CardHeader>

      {/* Progress Indicator */}
      <div style={{ marginBottom: '2rem' }}>        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          {operation.steps.map((s, index) => {
            const isCompleted = completedSteps.has(s.id);
            const isCurrent = index === currentStep;
            const isAccessible = index <= currentStep || isCompleted;
            
            return (
              <div
                key={s.id}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '0.5rem',
                  backgroundColor: isCompleted ? '#28a745' : isCurrent ? '#ffd700' : '#333',
                  color: isCompleted ? '#fff' : isCurrent ? '#000' : '#888',
                  cursor: isAccessible && !isProcessing ? 'pointer' : 'default',
                  fontSize: '0.8rem',
                  fontWeight: isCurrent ? 'bold' : 'normal',
                  position: 'relative'
                }}
                // Frozen while a tx is pending — navigating mid-transaction lands the
                // completion state on a step the user is no longer looking at.
                onClick={() => isAccessible && !isProcessing && goToStep(index)}
              >
                {isCompleted && <span style={{ marginRight: '0.25rem' }}>✓</span>}
                {index + 1}. {s.title}
              </div>
            );
          })}
        </div>
        <div style={{ 
          width: '100%', 
          height: '4px', 
          backgroundColor: '#333',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div 
            style={{
              width: `${((currentStep + 1) / operation.steps.length) * 100}%`,
              height: '100%',
              backgroundColor: '#ffd700',
              transition: 'width 0.3s ease'
            }}
          />
        </div>
      </div>      {/* Current Step */}
      {step && (
        <div style={{ marginBottom: '2rem' }}>
          <h3>{step.title}</h3>
          <p>{step.description}</p>
        
        {/* Amount validation warning for non-input steps (hope needs no amount) */}
        {step.action !== 'input' && step.action !== 'borrow-input' && step.action !== 'complete' && step.action !== 'hope' && (!amount || amount.trim() === '' || parseFloat(amount) <= 0) && (
          <Alert variant="warning" style={{ marginBottom: '1rem' }}>
            ⚠️ <strong>Amount Missing:</strong> Please go back to the input step and enter a valid amount, or use the "Complete" buttons from the main menu with pre-filled amounts.
          </Alert>
        )}
        
        {/* Show amount for non-input steps (hope is a pure permission grant) */}
        {step.action !== 'input' && step.action !== 'borrow-input' && step.action !== 'complete' && step.action !== 'hope' && amount && parseFloat(amount) > 0 && (
          <Alert variant="info" style={{ marginBottom: '1rem', backgroundColor: '#2c3e50', border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>💰</span>
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Amount: {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} {displayUnit}
              </span>
            </div>
            {step.action === 'join' && step.id === 'join-pls' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will transfer {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} PLS from your wallet to the ETHJoin contract.
              </div>
            )}
            {step.action === 'frob' && currentOperation === 'deposit-pls' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will move {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} PLS from ETHJoin to your vault as collateral.
              </div>
            )}
            {step.action === 'frob' && currentOperation === 'withdraw-pls' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will move {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} PLS from your vault to ETHJoin for withdrawal.
              </div>
            )}
            {step.action === 'exit' && step.id === 'exit-pls' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will transfer {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} PLS from ETHJoin to your wallet.
              </div>
            )}
            {step.action === 'approve' && step.id === 'approve-jdai' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will approve the system to transfer up to {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI from your wallet.
              </div>
            )}
            {step.action === 'join' && step.id === 'join-jdai' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will transfer {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI from your wallet to the DaiJoin contract.
              </div>
            )}
            {step.action === 'exit' && step.id === 'exit-jdai' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will transfer {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI from DaiJoin to your wallet.
              </div>
            )}
            {step.action === 'frob' && currentOperation === 'mint-jdai' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will create {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI debt in your vault.
              </div>
            )}            {step.action === 'frob' && currentOperation === 'repay-jdai' && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                This will reduce your vault debt by {parseFloat(displayAmount).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI.
              </div>
            )}
          </Alert>
        )}
        
        {/* Show approved amount for completed approval steps */}
        {completedSteps.has(step.id) && step.action === 'approve' && approvedAmounts[step.id] && (
          <Alert variant="info" style={{ marginBottom: '1rem', backgroundColor: '#2c3e50', border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span>
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Approved: {parseFloat(approvedAmounts[step.id]).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} {step.id.includes('pls') ? 'PLS' : 'JDAI'}
              </span>
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
              The system is authorized to transfer up to this amount from your wallet.
            </div>
          </Alert>
        )}
        
        {/* Show internal balance amounts for completed join steps */}
        {completedSteps.has(step.id) && step.action === 'join' && step.id === 'join-pls' && parseFloat(internalBalances.gem) > 0 && (
          <Alert variant="info" style={{ marginBottom: '1rem', backgroundColor: '#2c3e50', border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span>
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Deposited: {parseFloat(internalBalances.gem).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} PLS
              </span>
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
              This PLS is now in the ETHJoin contract and ready to be moved to your vault.
            </div>
          </Alert>
        )}
          {completedSteps.has(step.id) && step.action === 'join' && step.id === 'join-jdai' && parseFloat(internalBalances.dai) > 0 && (
          <Alert variant="info" style={{ marginBottom: '1rem', backgroundColor: '#2c3e50', border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span>
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                Deposited: {parseFloat(internalBalances.dai).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} JDAI
              </span>
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
              This JDAI is now in the DaiJoin contract and ready to be used for debt repayment.
            </div>
          </Alert>
        )}
        
        {/* Show vault balance amounts for completed frob steps */}
        {completedSteps.has(step.id) && step.action === 'frob' && (
          <Alert variant="info" style={{ marginBottom: '1rem', backgroundColor: '#2c3e50', border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span>
              <div>                {step.id === 'frob-deposit' && (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Vault Updated: Added {parseFloat(displayAmount).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} PLS collateral
                    </div>
                    <div style={{ marginTop: '0.3rem', fontSize: '0.9rem', opacity: 0.8 }}>
                      Your vault now has {parseFloat(vaultData.collateral).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} PLS total collateral.
                    </div>
                  </>
                )}
                {step.id === 'frob-withdraw' && (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Vault Updated: Removed {parseFloat(displayAmount).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} PLS collateral
                    </div>
                    <div style={{ marginTop: '0.3rem', fontSize: '0.9rem', opacity: 0.8 }}>
                      Your vault now has {parseFloat(vaultData.collateral).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} PLS total collateral.
                    </div>
                  </>
                )}                {step.id === 'frob-mint' && (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Vault Updated: Generated {parseFloat(displayAmount).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} JDAI debt
                    </div>
                    <div style={{ marginTop: '0.3rem', fontSize: '0.9rem', opacity: 0.8 }}>
                      Your vault now has {parseFloat(vaultData.debt).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} JDAI total debt.
                    </div>
                  </>
                )}
                {step.id === 'frob-repay' && (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Vault Updated: Reduced debt by {parseFloat(displayAmount).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} JDAI
                    </div>
                    <div style={{ marginTop: '0.3rem', fontSize: '0.9rem', opacity: 0.8 }}>
                      Your vault now has {parseFloat(vaultData.debt).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} JDAI total debt.
                    </div>
                  </>
                )}
              </div>
            </div>
          </Alert>
        )}
        
        {/* Show withdrawal amounts for completed exit steps */}
        {completedSteps.has(step.id) && step.action === 'exit' && (
          <Alert variant="info" style={{ marginBottom: '1rem', backgroundColor: '#2c3e50', border: '1px solid #3498db' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span>
              <div>
                {step.id === 'exit-pls' && (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Withdrawn: {parseFloat(displayAmount).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} PLS
                    </div>
                    <div style={{ marginTop: '0.3rem', fontSize: '0.9rem', opacity: 0.8 }}>
                      This PLS has been returned to your wallet.
                    </div>
                  </>
                )}
                {step.id === 'exit-jdai' && (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      Withdrawn: {parseFloat(displayAmount).toLocaleString('en-US', { 
                        minimumFractionDigits: 6, 
                        maximumFractionDigits: 6 
                      })} JDAI
                    </div>
                    <div style={{ marginTop: '0.3rem', fontSize: '0.9rem', opacity: 0.8 }}>
                      This JDAI has been returned to your wallet.
                    </div>
                  </>
                )}
              </div>
            </div>
          </Alert>
        )}
        
        {completedSteps.has(step.id) && (
          <Alert variant="success" style={{ marginBottom: '1rem' }}>
            ✅ This step has been completed.            {step.action === 'approve' && approvedAmounts[step.id] && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                <strong>Approved Amount:</strong> {parseFloat(approvedAmounts[step.id]).toLocaleString('en-US', { 
                  minimumFractionDigits: 6, 
                  maximumFractionDigits: 6 
                })} {step.id.includes('pls') ? 'PLS' : 'JDAI'}
              </div>
            )}
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              You can proceed to the next step or verify your progress.
            </div>
          </Alert>
        )}        {step.action === 'input' && (
          <Form onSubmit={(e) => e.preventDefault()}>
            {/* Auto-detected amount suggestion for other operations */}
            {currentOperation !== 'repay-jdai' && (() => {
              const suggestion = detectSuggestedAmount();
              if (suggestion && !amount) {
                return (
                  <Alert variant="info" style={{ marginBottom: '1rem' }}>
                    <h4>💡 Suggested Amount</h4>
                    <p>{suggestion.reason}</p>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>                      <Button
                        onClick={() => setAmount(suggestion.amount)}
                        variant="primary"
                      >
                        Use {parseFloat(suggestion.amount).toLocaleString('en-US', { 
                          minimumFractionDigits: 6, 
                          maximumFractionDigits: 6 
                        })} {currentOperation.includes('pls') ? 'PLS' : 'JDAI'}
                      </Button>
                      <Button
                        onClick={() => {/* User can manually enter amount instead */}}
                        variant="secondary"
                      >
                        Enter Different Amount
                      </Button>
                    </div>
                  </Alert>
                );
              }
              return null;
            })()}
              <InputGroup>              <Label>
                Amount {currentOperation.includes('pls') ? 'PLS' : 'JDAI'}
              </Label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>                <Input
                  type="number"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  step="0.000001"
                  min="0"
                />
                <Button
                  type="button"
                  onClick={() => setAmount(getMaxAmount().toString())}
                  variant="secondary"
                >
                  Max
                </Button>
              </div>
            </InputGroup>
          </Form>
        )}        {step.action === 'borrow-input' && (() => {
          const mat = parseFloat(systemData.liquidationRatio) || 1.5;
          const plan = borrowRaw && amount && parseFloat(amount) > 0
            ? borrowPlan(toWad(amount), numberToWad(safetyPct / 100), numberToWad(mat), borrowRaw)
            : null;
          const depositNum = plan ? wadToNumber(plan.additionalInkWad) : 0;
          const lockPrice = plan
            ? wadToNumber(liquidationPriceWad(
                borrowRaw.ink + plan.additionalInkWad,
                plan.newDebtWad,
                numberToWad(parseFloat(systemData.oracleJdaiTarget) || 0),
                numberToWad(mat) * (10n ** 9n) // WAD -> RAY
              ))
            : 0;
          const dropPct = Math.max(0, (1 - mat / (safetyPct / 100)) * 100);
          const walletPls = parseFloat(balances.pls);
          const shortOnPls = plan && depositNum > 0 && depositNum + gasReservePls > walletPls;
          return (
            <Form onSubmit={(e) => e.preventDefault()}>
              <InputGroup>
                <Label>How much JDAI do you want?</Label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Input
                    type="number"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="10"
                    step="1"
                    min="0"
                  />
                  <Button
                    type="button"
                    onClick={() => setAmount(getMaxAmount().toString())}
                    variant="secondary"
                  >
                    Max
                  </Button>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                  Minimum vault debt: {formatNumber(systemData.minDebt, 0)} JDAI · ceiling room: {formatNumber(systemData.availableDebt, 0)} JDAI
                </div>
              </InputGroup>

              <InputGroup>
                <Label>Safety level — how far PLS can fall before your vault locks</Label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {[170, 200, 300].map((pct) => (
                    <Button
                      key={pct}
                      type="button"
                      variant={safetyPct === pct ? undefined : 'secondary'}
                      onClick={() => setSafetyPct(pct)}
                      style={{ flex: 1, padding: '0.75rem 0.5rem', minWidth: '9rem' }}
                    >
                      {pct}%
                      <div style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
                        {pct === 170 ? 'tight' : pct === 200 ? 'balanced' : 'conservative'} · locks after ~{Math.round((1 - mat / (pct / 100)) * 100)}% drop
                      </div>
                    </Button>
                  ))}
                </div>
              </InputGroup>

              {!borrowRaw && (
                <Alert variant="info">Loading vault data from the chain…</Alert>
              )}
              {plan && (
                <Alert variant="info">
                  <div className="title">Your plan</div>
                  <div className="message">
                    <div>Collateral needed at {safetyPct}%: <strong>{formatNumber(wadToNumber(plan.requiredInkWad), 0)} PLS</strong></div>
                    <div>Already in your vault: {formatNumber(wadToNumber(borrowRaw.ink), 0)} PLS</div>
                    <div>
                      Deposit now: <strong style={{ color: '#ffd700' }}>{formatNumber(depositNum, 0)} PLS</strong>
                      {depositNum === 0 ? ' — your current collateral already covers this!' : ''}
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                      Vault locks if PLS falls below ≈ ${lockPrice.toFixed(9)} (~{dropPct.toFixed(0)}% below the current oracle price)
                    </div>
                    {shortOnPls && (
                      <div style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                        ⚠️ Your wallet holds {formatNumber(walletPls, 0)} PLS — this plan needs {formatNumber(depositNum, 0)} plus ~{formatNumber(gasReservePls, 0)} kept for gas. Lower the JDAI amount or the safety level.
                      </div>
                    )}
                  </div>
                </Alert>
              )}
            </Form>
          );
        })()}        {step.action === 'complete' && (
          <Alert variant="success">
            <h3>✅ Operation Complete!</h3>
            <p>{step.description}</p>
          </Alert>
        )}
        </div>
      )}

      {/* Asset Location Display */}
      <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#222', borderRadius: '8px' }}>
        <h4>Asset Locations</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <strong>Your Wallet</strong>
            <div>PLS: {formatNumber(balances.pls, 2)}</div>
            <div>JDAI: {formatNumber(balances.jdai, 2)}</div>
          </div>          <div>
            <strong>Internal Balances</strong>
            <div>PLS: {formatNumber(internalBalances.gem, 2)}</div>
            <div>DAI: {formatNumber(internalBalances.dai, 2)}</div>
          </div>
          <div>
            <strong>Vault</strong>
            <div>Collateral: {formatNumber(vaultData.collateral, 2)} PLS</div>
            <div>Debt: {formatNumber(vaultData.debt, 2)} JDAI</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
        <Button
          onClick={goBack}
          disabled={isProcessing}
          variant="secondary"
        >
          ← Back
        </Button>        {step.action === 'input' && (
          <Button
            onClick={() => {
              const floorMsg = debtFloorViolation(currentOperation, amount, vaultData.debt, systemData.minDebt);
              if (floorMsg) { toast.error(floorMsg); return; }
              setCurrentStep(currentStep + 1);
            }}
            disabled={!amount || parseFloat(amount) <= 0}
          >
            Continue →
          </Button>
        )}        {step.action === 'borrow-input' && (
          <Button
            onClick={handleBorrowContinue}
            disabled={!amount || parseFloat(amount) <= 0 || !borrowRaw}
          >
            Continue →
          </Button>
        )}        {step.action !== 'input' && step.action !== 'borrow-input' && step.action !== 'complete' && (
          <div style={{ marginTop: '1rem' }}>
            {completedSteps.has(step.id) ? (
              // Step is already completed - show status instead of action button
              <Alert variant="success" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>✅</span>
                  <span><strong>{step.title}</strong> - Already completed</span>
                </div>
                {step.action === 'approve' && approvedAmounts[step.id] && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    Approved: {formatNumber(approvedAmounts[step.id], 2)} {step.id.includes('pls') ? 'PLS' : 'JDAI'}
                  </div>
                )}
                {step.action === 'join' && step.id === 'join-pls' && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                    {formatNumber(internalBalances.gem, 2)} PLS in internal balance
                  </div>
                )}
              </Alert>
            ) : (
              // Step not completed - show action button (held while the auto-skip
              // effect checks the chain, so a fast click can't sign a redundant tx)
              <Button
                onClick={executeStep}
                disabled={isProcessing || checkingAuth}
                style={{ marginRight: '1rem' }}
              >
                {isProcessing ? <LoadingSpinner />
                  : checkingAuth ? 'Checking authorization…'
                  : step.title}
              </Button>
            )}
          </div>
        )}        {step.action === 'complete' && (
          <Button onClick={resetOperation}>
            Done
          </Button>
        )}
      </div>
    </Card>
  );
};

export default VaultActionsV2;
